import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, MailCheck } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { authApi } from '@/api/auth';
import { Field } from '@/components/ui/field';
import { ApiError } from '@/lib/api-client';
import { verifyEmailSchema, type VerifyEmailInput } from '@/lib/form-schemas';
import { qk } from '@/lib/query-keys';

export function VerifyEmailPage(): JSX.Element {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [search] = useSearchParams();
  const devCode = search.get('devCode') ?? '';
  const [submitError, setSubmitError] = useState<string | null>(null);

  const form = useForm<VerifyEmailInput>({
    resolver: zodResolver(verifyEmailSchema),
    defaultValues: { code: devCode },
    mode: 'onSubmit',
  });

  const me = useQuery({
    queryKey: qk.auth.me,
    queryFn: () => authApi.me(),
    retry: false,
  });

  useEffect(() => {
    if (me.data?.email_verified_at) {
      navigate('/dashboard', { replace: true });
    }
  }, [me.data, navigate]);

  const verify = useMutation({
    mutationFn: (code: string) => authApi.verifyEmail(code),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: qk.auth.me });
      navigate('/dashboard', { replace: true });
    },
  });

  const onSubmit = form.handleSubmit(async (values) => {
    setSubmitError(null);
    try {
      await verify.mutateAsync(values.code);
    } catch (err) {
      setSubmitError(err instanceof ApiError ? err.message : 'Verification failed');
    }
  });

  return (
    <div className="min-h-screen flex items-center justify-center bg-mb-bg p-6">
      <div className="mb-card p-10 max-w-md w-full">
        <div className="flex flex-col items-center text-center">
          <div className="h-12 w-12 rounded-lg bg-mb-accent-soft text-mb-accent-press flex items-center justify-center mb-4">
            <MailCheck className="h-5 w-5" strokeWidth={1.75} />
          </div>
          <h1 className="font-display text-xl font-bold text-mb-text">Check your email</h1>
          <p className="text-sm text-mb-text-muted mt-1">
            We sent a 6-digit code to{' '}
            <span className="font-mono text-mb-text">{me.data?.email ?? '…'}</span>.
          </p>
        </div>

        {devCode ? (
          <div className="mt-6 rounded-md border border-mb-border bg-mb-surface p-3 text-xs text-mb-text-muted">
            No sending mailbox is connected yet, so the system couldn&rsquo;t email this
            code. We pre-filled it below — once verified, connect a mailbox so future
            codes arrive by email.
          </div>
        ) : null}

        <form onSubmit={onSubmit} className="mt-6 space-y-3" noValidate>
          <Field label="6-digit code" required error={form.formState.errors.code?.message}>
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              className="mb-input text-center text-2xl tracking-[0.5em] font-mono"
              placeholder="••••••"
              {...form.register('code', {
                onChange: (e) => {
                  const cleaned = e.target.value.replace(/\D/g, '').slice(0, 6);
                  if (cleaned !== e.target.value) form.setValue('code', cleaned);
                },
              })}
            />
          </Field>
          {submitError ? (
            <div role="alert" className="rounded-md border border-mb-danger/30 bg-mb-danger-soft p-2 text-xs text-mb-danger">
              {submitError}
            </div>
          ) : null}
          <button
            type="submit"
            disabled={verify.isPending || form.formState.isSubmitting}
            className="mb-btn-primary w-full"
          >
            {verify.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Verify and continue
          </button>
        </form>
      </div>
    </div>
  );
}
